/**
 * The company materiality matrix, in the chat matrix card's design (`mcp-apps/dma-matrix`): two
 * views of one assessment behind a switch, topics first.
 *
 * - Topics: a 2×2 of impact materiality against financial materiality, one entry per ESRS topic.
 *   Hovering or focusing a material topic lists the matters that make it material, and on which
 *   axis; clicking it opens the Matters view filtered to that topic.
 * - Matters: the 3×3 of impact against financial materiality levels, each material matter placed
 *   per valence. The low × low cell is a count. A topic filter narrows the matters.
 *
 * The topics and placements come from the same backend code as the card's, so the two agree.
 * `isStatic` draws it for the image export: no controls, no links, and a header naming the
 * company and the view.
 */
import { SegmentedControl } from '@primer/react';
import type { DmaMatrixTopic, DmaMatrixValence } from '@upright/common/mcp-payloads/dma-matrix';
import type { MaterialityStatus } from '@upright/common/mcp-payloads/types';
import { getMatterById } from '@upright/common/model/input/materiality-assessment/matters';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { GetCompanyMaterialityMatrixResponse } from '@upright/common/model/output/materiality/materiality-matrix/api-types';
import { CompanyMaterialityMatrixView } from '@upright/common/renderer/component-types/types-render-view-csrd-matriality-matrix';
import { Link } from '@upright/ui/common/generic/components/link';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import clsx from 'clsx';
import { Fragment, ReactNode, useMemo } from 'react';
import styles from './topic-matter-matrix.module.css';
import { TopicFilter } from './topic-filter';
import {
  countLowOrNoMateriality,
  FINANCIAL_LEVELS_LEFT_RIGHT,
  getCellPlacements,
  getSelectableTopicIds,
  IMPACT_LEVELS_TOP_DOWN,
} from './utils';

const VIEWS: Array<{ id: CompanyMaterialityMatrixView; label: string }> = [
  { id: 'topics', label: 'Topics' },
  { id: 'matters', label: 'Matters' },
];
const LEVEL_LABEL = { low: 'Low', medium: 'Medium', high: 'High' } as const;
const IMPACT_STATUSES: MaterialityStatus[] = ['material', 'not-material'];
const FINANCIAL_STATUSES: MaterialityStatus[] = ['not-material', 'material'];
const STATUS_LABEL: Record<MaterialityStatus, string> = { material: 'Material', 'not-material': 'Not material' };
const MATERIAL_ON_LABEL = {
  impact: 'Impact material',
  financial: 'Financially material',
  'impact-and-financial': 'Impact and financially material',
} as const;

interface Props {
  data: GetCompanyMaterialityMatrixResponse;
  view: CompanyMaterialityMatrixView;
  onViewChange?: (view: CompanyMaterialityMatrixView) => void;
  /** The Matters view's topic filter; empty is every topic. */
  selectedTopicIds: readonly string[];
  onSelectedTopicIdsChange?: (topicIds: string[]) => void;
  /** The matter page a placement opens. Absent: the matters are not links. */
  getMatterHref?: (matterId: SustainabilityMatterId) => string;
  /** Beside the legend, such as the image export. */
  actions?: ReactNode;
  /** The image export: the company's name over the matrix, and nothing interactive. */
  isStatic?: boolean;
  entityName?: string;
}

export function TopicMatterMatrix(props: Props) {
  const { data, selectedTopicIds, isStatic } = props;
  // A frontend served against an older backend gets no topics: it can still draw the matters.
  const topics = data.topics;
  const view = topics ? props.view : 'matters';
  const selectableTopicIds = useMemo(
    () => (topics ? getSelectableTopicIds(topics, data.matrixData) : new Set<string>()),
    [topics, data.matrixData],
  );

  const showTopicMatters = (topicId: string) => {
    props.onSelectedTopicIdsChange?.([topicId]);
    props.onViewChange?.('matters');
  };
  const count =
    data.totalMatterCount === undefined
      ? `${data.materialMatterCount} material matters`
      : `${data.materialMatterCount} of ${data.totalMatterCount} matters material`;

  return (
    <div className={styles.root}>
      {isStatic ? (
        <div className={styles.staticHeader}>
          <div className={styles.staticTitle}>
            {props.entityName && <span className={styles.entityName}>{props.entityName}</span>}
            <span className={styles.count}>
              {staticSubtitle(view, topics ?? [], selectedTopicIds)} · {count}
            </span>
          </div>
          <Legend />
        </div>
      ) : (
        <div className={styles.controls}>
          <div className={styles.controlsStart}>
            {topics && (
              <SegmentedControl
                size="small"
                aria-label="Materiality matrix view"
                onChange={index => {
                  props.onViewChange?.(VIEWS[index].id);
                }}
              >
                {VIEWS.map(option => (
                  <SegmentedControl.Button key={option.id} selected={view === option.id}>
                    {option.label}
                  </SegmentedControl.Button>
                ))}
              </SegmentedControl>
            )}
            {topics && view === 'matters' && (
              <TopicFilter
                topics={topics}
                selectableTopicIds={selectableTopicIds}
                selectedTopicIds={selectedTopicIds}
                onChange={topicIds => {
                  props.onSelectedTopicIdsChange?.(topicIds);
                }}
              />
            )}
            <span className={styles.count}>{count}</span>
          </div>
          <div className={styles.controlsEnd}>
            <Legend />
            {props.actions}
          </div>
        </div>
      )}
      {view === 'topics' && topics ? (
        <TopicMatrix
          topics={topics}
          canShowMatters={!isStatic}
          selectableTopicIds={selectableTopicIds}
          onShowTopicMatters={showTopicMatters}
        />
      ) : (
        <MatterMatrix
          data={data}
          selectedTopicIds={selectedTopicIds}
          getMatterHref={isStatic ? undefined : props.getMatterHref}
        />
      )}
    </div>
  );
}

function staticSubtitle(
  view: CompanyMaterialityMatrixView,
  topics: DmaMatrixTopic[],
  selectedTopicIds: readonly string[],
): string {
  if (view === 'topics') return 'Materiality matrix by topic';
  const names = topics.filter(t => selectedTopicIds.includes(t.topicId)).map(t => t.topicName);
  return names.length ? `Materiality matrix by matter: ${names.join(', ')}` : 'Materiality matrix by matter';
}

function Swatch({ valence }: { valence: DmaMatrixValence }) {
  return <span className={clsx(styles.swatch, styles[valence])} aria-hidden="true" />;
}

function Legend() {
  return (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <Swatch valence="positive" />
        Positive
      </span>
      <span className={styles.legendItem}>
        <Swatch valence="negative" />
        Negative
      </span>
      <span className={styles.legendItem}>
        <Swatch valence="positive-and-negative" />
        Positive and negative
      </span>
    </div>
  );
}

function MatterName({ matterId }: { matterId: SustainabilityMatterId }) {
  const { title, qualifier } = getMatterById(matterId);
  return (
    <span className={styles.itemText}>
      {title}
      {qualifier && <span className={styles.qualifier}> ({qualifier})</span>}
    </span>
  );
}

/** The outer border and the rounded outer corners of an n × n grid. */
function cellClassName(row: number, col: number, size: number, isQuiet: boolean): string {
  return clsx(styles.cell, {
    [styles.firstRow]: row === 0,
    [styles.firstCol]: col === 0,
    [styles.topLeft]: row === 0 && col === 0,
    [styles.topRight]: row === 0 && col === size - 1,
    [styles.bottomLeft]: row === size - 1 && col === 0,
    [styles.bottomRight]: row === size - 1 && col === size - 1,
    [styles.quiet]: isQuiet,
  });
}

// ─── Topic view ──────────────────────────────────────────────────────────────

function TopicMatrix(props: {
  topics: DmaMatrixTopic[];
  canShowMatters: boolean;
  selectableTopicIds: ReadonlySet<string>;
  onShowTopicMatters: (topicId: string) => void;
}) {
  return (
    <div className={clsx(styles.matrix, styles.topics)} role="group" aria-label="Materiality matrix by topic">
      <div className={styles.yAxis}>Impact materiality &rarr;</div>
      {IMPACT_STATUSES.map((impact, row) => (
        <Fragment key={impact}>
          <div className={styles.yLevel} style={{ gridRow: row + 1 }}>
            {STATUS_LABEL[impact]}
          </div>
          {FINANCIAL_STATUSES.map((financial, col) => (
            <div
              key={financial}
              className={cellClassName(row, col, 2, impact === 'not-material' && financial === 'not-material')}
              style={{ gridRow: row + 1, gridColumn: col + 3 }}
            >
              {props.topics
                .filter(t => t.impactMateriality === impact && t.financialMateriality === financial)
                .map(topic => (
                  <TopicItem
                    key={topic.topicId}
                    topic={topic}
                    onShowMatters={
                      props.canShowMatters && props.selectableTopicIds.has(topic.topicId)
                        ? props.onShowTopicMatters
                        : undefined
                    }
                  />
                ))}
            </div>
          ))}
        </Fragment>
      ))}
      {FINANCIAL_STATUSES.map((financial, col) => (
        <div key={financial} className={styles.xLevel} style={{ gridColumn: col + 3 }}>
          {STATUS_LABEL[financial]}
        </div>
      ))}
      <div className={styles.xAxis}>Financial materiality &rarr;</div>
    </div>
  );
}

function TopicItem(props: { topic: DmaMatrixTopic; onShowMatters?: (topicId: string) => void }) {
  const { topic, onShowMatters } = props;
  if (!topic.valence) {
    return (
      <div className={clsx(styles.item, styles.muted)}>
        <span className={styles.swatchSpacer} aria-hidden="true" />
        <span className={styles.itemText}>{topic.topicName}</span>
      </div>
    );
  }
  const content = (
    <>
      <Swatch valence={topic.valence} />
      <span className={styles.itemText}>{topic.topicName}</span>
    </>
  );
  if (!onShowMatters) return <div className={styles.item}>{content}</div>;
  return (
    // The link takes no class, so its hover and focus states are styled from this wrapper.
    <span className={styles.entryTrigger}>
      <Tooltip theme="light" placement="bottom start" text={<TopicMattersTooltip topic={topic} />}>
        <Link
          color="inherit"
          onPress={() => {
            onShowMatters(topic.topicId);
          }}
        >
          <span className={styles.item}>{content}</span>
        </Link>
      </Tooltip>
    </span>
  );
}

function TopicMattersTooltip({ topic }: { topic: DmaMatrixTopic }) {
  return (
    <span className={styles.tooltip}>
      <span className={styles.tooltipTitle}>{topic.topicName}</span>
      {topic.materialMatters.map(entry => (
        <span key={entry.matterId} className={styles.tooltipRow}>
          {entry.valence ? <Swatch valence={entry.valence} /> : <span className={styles.swatchSpacer} />}
          <MatterName matterId={entry.matterId as SustainabilityMatterId} />
          <span className={styles.tooltipMeta}>{MATERIAL_ON_LABEL[entry.materialOn]}</span>
        </span>
      ))}
    </span>
  );
}

// ─── Matter view ─────────────────────────────────────────────────────────────

function MatterMatrix(props: {
  data: GetCompanyMaterialityMatrixResponse;
  selectedTopicIds: readonly string[];
  getMatterHref?: (matterId: SustainabilityMatterId) => string;
}) {
  const { data, selectedTopicIds, getMatterHref } = props;
  const lowOrNoMaterialityCount = countLowOrNoMateriality(data.matrixData, selectedTopicIds);
  return (
    <div className={clsx(styles.matrix, styles.matters)} role="group" aria-label="Materiality matrix by matter">
      <div className={styles.yAxis}>Impact materiality &rarr;</div>
      {IMPACT_LEVELS_TOP_DOWN.map((impact, row) => (
        <Fragment key={impact}>
          <div className={styles.yLevel} style={{ gridRow: row + 1 }}>
            {LEVEL_LABEL[impact]}
          </div>
          {FINANCIAL_LEVELS_LEFT_RIGHT.map((financial, col) => {
            const isLowLow = impact === 'low' && financial === 'low';
            return (
              <div
                key={financial}
                className={cellClassName(row, col, 3, isLowLow)}
                style={{ gridRow: row + 1, gridColumn: col + 3 }}
              >
                {isLowLow ? (
                  <span className={styles.lowLow}>
                    {lowOrNoMaterialityCount} {lowOrNoMaterialityCount === 1 ? 'matter' : 'matters'} with low or no
                    materiality
                  </span>
                ) : (
                  getCellPlacements(data.matrixData, impact, financial, selectedTopicIds).map(placement => (
                    <MatterItem
                      key={`${placement.matterId}-${placement.valence}`}
                      matterId={placement.matterId}
                      valence={placement.valence}
                      href={getMatterHref?.(placement.matterId)}
                    />
                  ))
                )}
              </div>
            );
          })}
        </Fragment>
      ))}
      {FINANCIAL_LEVELS_LEFT_RIGHT.map((financial, col) => (
        <div key={financial} className={styles.xLevel} style={{ gridColumn: col + 3 }}>
          {LEVEL_LABEL[financial]}
        </div>
      ))}
      <div className={styles.xAxis}>Financial materiality &rarr;</div>
    </div>
  );
}

function MatterItem(props: { matterId: SustainabilityMatterId; valence: DmaMatrixValence; href?: string }) {
  const content = (
    <span className={styles.item}>
      <Swatch valence={props.valence} />
      <MatterName matterId={props.matterId} />
    </span>
  );
  if (!props.href) return content;
  return (
    <span className={styles.entryTrigger}>
      <Link href={props.href} color="inherit">
        {content}
      </Link>
    </span>
  );
}
